import BaseController from "niteroi/packages/system/controllers/Base.controller";
import TableSelectDialog, {
    TableSelectDialog$ConfirmEvent,
    TableSelectDialog$SearchEvent
} from "sap/m/TableSelectDialog";
import Event from "sap/ui/base/Event";
import ListBinding from "sap/ui/model/ListBinding";
import FilterOperator from "sap/ui/model/FilterOperator";
import Filter from "sap/ui/model/Filter";
import {Input$SuggestionItemSelectedEvent, Input$ValueHelpRequestEvent} from "sap/m/Input";
import {IBusinessPartner} from "niteroi/packages/sales-distribution/models/interfaces";
import ColumnListItem from "sap/m/ColumnListItem";

export default class Main extends BaseController {
    public onInit(): void {
        super.onInit();
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();
    }

    protected loadCollectionData(path: string, modelName: string): void {
        this.getView().setBusy(true);
        const model = this.createRequestModel();
        model.setData([]);
        this.setModel(model, modelName);
        model.loadData(`/api/sales-distribution/${path}`, "", true, "GET", false, true, this.getRequestHeaders())
            .finally(() => {
                this.getView().setBusy(false);
            });
    }

    /**
     * Opens the Value Help Dialog when the F4 / input icon is clicked.
     */
    public onValueHelpRequestBusinessPartner(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("soldToPartnerValueHelpDialog") as TableSelectDialog;
        if (dialog) {
            event.getSource().setBusy(true);
            const businessPartnersModel = this.getModel("salesDistributionBusinessPartnerCollection");
            if (businessPartnersModel) {
                businessPartnersModel.loadData(
                    "/api/sales-distribution/business-partners",
                    "",
                    true,
                    "GET",
                    true,
                    true,
                    this.getRequestHeaders()
                ).then(() => {
                    dialog.open(event.getSource().getValue());
                }).finally(() => {
                    event.getSource().setBusy(false);
                });
            }
        }
    }

    /**
     * Filters the TableSelectDialog rows based on the user's search query.
     */
    public onValueHelpSearchBusinessPartner(event: TableSelectDialog$SearchEvent): void {
        const value = event.getParameter("value") || "";
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;

        // Base filter to ensure blocked partners remain hidden
        const blockedFilter = new Filter("isActive", FilterOperator.EQ, true);

        if (!value) {
            binding.filter([blockedFilter]);
            return;
        }

        // Search across multiple fields simultaneously (OR condition)
        const searchFilters = [
            new Filter("customerNumber", FilterOperator.Contains, value),
            new Filter("legalName1", FilterOperator.Contains, value),
            new Filter("legalName2", FilterOperator.Contains, value),
            new Filter("vatId", FilterOperator.Contains, value),
            new Filter("taxNumber", FilterOperator.Contains, value)
        ];

        const combinedSearchFilter = new Filter({
            filters: searchFilters,
            and: false
        });

        // Combine the security filter AND the search filters
        const finalFilter = new Filter({
            filters: [blockedFilter, combinedSearchFilter],
            and: true
        });

        binding.filter([finalFilter]);
    }

    /**
     * Handles selection made directly from the type-ahead suggestion dropdown.
     */
    public onSuggestionItemSelectedBusinessPartner(event: Input$SuggestionItemSelectedEvent): void {
        const row = event.getParameter("selectedRow") as ColumnListItem;
        if (!row) {
            return;
        }
        const context = row.getBindingContext("salesDistributionBusinessPartnerCollection");
        if (context) {
            this._updatePartner(context.getObject() as IBusinessPartner);
        }
    }

    /**
     * To be overwritten by the components.
     *
     * @private
     * @param _
     */
    protected _updatePartner(_: IBusinessPartner): void {}

    /**
     * Confirms selection from the Value Help Dialog and populates the ID.
     */
    public onValueHelpConfirmBusinessPartner(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItem = event.getParameter("selectedItem");
        if (selectedItem) {
            const context = selectedItem.getBindingContext("salesDistributionBusinessPartnerCollection");
            if (context) {
                this._updatePartner(context.getObject() as IBusinessPartner);
            }
        }
        this._resetValueHelpDialogFilters(event);
    }

    /**
     * Cleans up filters if the user cancels or escapes out of the dialog.
     */
    public onValueHelpCancelBusinessPartner(event: Event): void {
        this._resetValueHelpDialogFilters(event);
    }

    /**
     * Internal helper to reset dialog filters back to default baseline
     */
    protected _resetValueHelpDialogFilters(event: Event): void {
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;
        const blockedFilter = new Filter("isActive", FilterOperator.EQ, true);
        binding?.filter([blockedFilter]);
    }
}
